import { useCallback, useEffect, useState } from 'react';
import { engineFetchJson } from '@/lib/engineHttp';

interface Signal {
  source: string;
  dimension: string;
  severity: 'critical' | 'high' | 'medium' | 'low' | 'info';
  title: string;
  detail: string;
  value?: unknown;
  confidence: number;
  timestamp: string;
  metadata?: Record<string, unknown>;
}

export function SignalsTab() {
  const [signals, setSignals] = useState<Signal[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hours, setHours] = useState(24);

  const load = useCallback(async (h: number) => {
    setLoading(true);
    setError(null);
    try {
      const data = await engineFetchJson<Signal[]>(`/signals/recent?hours=${h}`);
      // Sort signals by severity rank (critical = 5, high = 4, medium = 3, low = 2, info = 1)
      const severityRankMap: Record<string, number> = {
        critical: 5,
        high: 4,
        medium: 3,
        low: 2,
        info: 1,
      };
      const sorted = [...data].sort((a, b) => {
        const rankA = severityRankMap[a.severity] || 0;
        const rankB = severityRankMap[b.severity] || 0;
        if (rankB !== rankA) return rankB - rankA;
        return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
      });
      setSignals(sorted);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch signals');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(hours);
  }, [load, hours]);

  const severityStyles = (sev: string) => {
    switch (sev.toLowerCase()) {
      case 'critical':
        return 'text-[#FF0055] border-[#FF0055]/30 bg-[#FF0055]/10';
      case 'high':
        return 'text-orange-400 border-orange-400/30 bg-orange-400/10';
      case 'medium':
        return 'text-yellow-400 border-yellow-400/30 bg-yellow-400/10';
      case 'low':
        return 'text-blue-400 border-blue-400/30 bg-blue-400/10';
      default:
        return 'text-gray-400 border-gray-800 bg-gray-900/40';
    }
  };

  return (
    <div className="p-6 font-mono text-sm text-gray-200 space-y-4 overflow-auto h-full max-h-[calc(100vh-140px)]">
      <div className="flex items-center justify-between border-b border-gray-800 pb-3">
        <div>
          <div className="text-xs uppercase tracking-wider text-gray-500">Telemetry Feed</div>
          <div className="text-lg font-bold text-white mt-1">Signals Aggregation</div>
        </div>
        <div className="flex items-center gap-3 text-xs">
          <div className="flex items-center gap-2">
            <span className="text-gray-500">Range:</span>
            <select
              value={hours}
              onChange={(e) => {
                const val = Number(e.target.value);
                setHours(val);
              }}
              className="bg-gray-900 border border-gray-800 rounded px-2 py-1 text-gray-300 focus:outline-none focus:border-[#39FF14]/40"
            >
              <option value={12}>12 hours</option>
              <option value={24}>24 hours</option>
              <option value={72}>72 hours</option>
              <option value={168}>7 days</option>
            </select>
          </div>
          <button
            onClick={() => void load(hours)}
            disabled={loading}
            className="px-2 py-1 border border-gray-800 rounded hover:border-[#39FF14]/40 hover:text-[#39FF14] transition-colors disabled:opacity-50"
          >
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>

      {loading && !signals ? (
        <div className="text-xs text-gray-500 animate-pulse py-4">&gt; PULLING SIGNALS METRICS CHANNEL...</div>
      ) : error ? (
        <div className="bg-[#FF0055]/5 border border-[#FF0055]/20 p-4 rounded text-xs space-y-2">
          <p className="text-[#FF0055] font-bold">&gt; SIGNAL AGGREGATION FAILURE</p>
          <p className="text-gray-400">{error}</p>
        </div>
      ) : !signals || signals.length === 0 ? (
        <div className="border border-gray-800 p-8 rounded text-center text-xs text-gray-500 bg-gray-900/10">
          No operational signals captured in the specified window.
        </div>
      ) : (
        <div className="space-y-3">
          {signals.map((sig, idx) => (
            <div
              key={idx}
              className="bg-gray-900/30 border border-gray-800/80 rounded p-4 flex flex-col md:flex-row md:items-start justify-between gap-4 hover:border-gray-700/60 transition-colors"
            >
              <div className="space-y-1.5 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`px-2 py-0.5 border text-[10px] font-bold rounded uppercase tracking-wider ${severityStyles(sig.severity)}`}>
                    {sig.severity}
                  </span>
                  <span className="text-xs text-gray-400 font-semibold">{sig.source}</span>
                  <span className="text-xs text-gray-600 font-medium">[{sig.dimension}]</span>
                  {sig.confidence !== undefined && (
                    <span className="text-[10px] text-gray-500">
                      (conf: {(sig.confidence * 100).toFixed(0)}%)
                    </span>
                  )}
                </div>
                <h4 className="text-sm font-bold text-gray-100">{sig.title}</h4>
                <p className="text-xs text-gray-400 leading-relaxed max-w-4xl">{sig.detail}</p>
                {sig.value !== null && sig.value !== undefined && (
                  <div className="text-xs text-gray-500 mt-1">
                    Value: <span className="font-semibold text-gray-300">{JSON.stringify(sig.value)}</span>
                  </div>
                )}
              </div>
              <div className="text-[11px] text-gray-500 whitespace-nowrap self-end md:self-start">
                {new Date(sig.timestamp).toLocaleTimeString()}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
